import { Injectable } from '@angular/core';

import { AccountBalanceService, WalletSelectorDialogService } from '@frontend/sports/account-balance/feature';
import { RouterEventsService } from '@frontend/sports/common/core/utils/router-events';
import { ignoreElementsTyped } from '@frontend/sports/common/core/utils/rxjs';
import { DialogSettings, PopupAction } from '@frontend/sports/modal/feature';
import { RedirectHelperService } from '@frontend/sports/navigation/core/feature/redirect';
import { ModelPopupTypes, MyBetsUrlParam } from '@frontend/sports/navigation/core/feature/url-helper';
import { BetslipDisplayMode } from '@frontend/sports/types/components/bet-placement';
import { SyncUserSettingsService, UserActions } from '@frontend/sports/user/feature';
import { LocalStoreService, ProductHomepagesConfig } from '@frontend/vanilla/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { BetBuilderOverlayService } from 'packages/sports/web/app/src/bet-builder/services/bet-builder-overlay.service';
import { PopupManager } from 'packages/sports/web/app/src/popup/popup-manager.service';
import { UiManagerActions } from 'packages/sports/web/app/src/ui-manager/ui-manager.actions';
import { UiStates } from 'packages/sports/web/app/src/ui-manager/ui-states.enum';
import { concatMap, filter, map, merge, switchMap, tap } from 'rxjs';

import { betslipSelector, selectIsLinearBetslip } from '../../../base/store/selectors';
import { IBetslipState } from '../../../base/store/state';
import { BetslipType } from '../../../core/betslip-type';
import { BetslipBetBuilderPick } from '../../../core/picks/betslip-bet-builder-pick';
import { BaseBetslipGroupPick, BetslipGroupPick } from '../../../core/picks/betslip-group-pick';
import { BetslipPick } from '../../../core/picks/betslip-pick';
import { GroupEnabledPickIds } from '../../../core/picks/pick-id';
import { isBaseBetslipGroupPick, isBetslipBetBuilderPick } from '../../../core/utils';
import DigitalActions from '../../../digital/actions';
import { selectAffiliateOptionRequest } from '../../affiliates/state/affiliate.selectors';
import BetbuilderActions from '../../bet-builder/actions';
import BetslipPickActions from '../../picks/actions';
import { addGroup, removeAndReplaceGroup, removePicksFromGroup } from '../../picks/grouped-picks.actions';
import { ofTypeReplaceExistingPick } from '../../picks/helpers';
import { betslipPicksListSelector, selectBetslipFlattenedPicksList } from '../../picks/selectors';
import { BetslipResultActions } from '../../result/actions';
import { selectKeepsAnyBetsInBetslip } from '../../result/selectors';
import { RewardTokensActions } from '../../reward-tokens/actions';
import { BetslipTypesActions } from '../../types/actions';
import BetslipErrorsActions from '../../validation/actions';
import { isGroupPickOfferChange, isPickStatusLocked } from '../../validation/services/utils/betslip-errors-utils';
import { QuickBetActions } from '../quick-bet-actions';
import {
    quickBetStateSelector,
    selectIsQuickBetActive,
    selectQuickBetActionButtonProcessing,
    selectQuickBetDisplayHelper,
    selectQuickBetLockedOrClosedState,
    selectQuickBetPickIds,
    selectQuickBetPicksWithErrors,
    selectQuickBetStakeState,
    showQuickBetHelpSelector,
} from '../quick-bet.selectors';
import { IQuickBetStakeState, QuickBetActionButtonState, QuickBetStore, QuickBetUiState } from '../quick-bet.state';
import { QuickBetUtils } from '../services/quick-bet-utils.service';
import { QuickBetService } from '../services/quick-bet.service';

@Injectable()
export class QuickBetEffects {
    maxPickCount: number;

    constructor(
        private actions$: Actions,
        private store: Store,
        private quickBetUtils: QuickBetUtils,
        private quickBetService: QuickBetService,
        private redirectHelperService: RedirectHelperService,
        private productHomepagesConfig: ProductHomepagesConfig,
        private localStore: LocalStoreService,
        private accountBalanceService: AccountBalanceService,
        private walletSelectorDialogService: WalletSelectorDialogService,
        private routerEventsService: RouterEventsService,
        private userSettings: SyncUserSettingsService,
        private betBuilderOverlayService: BetBuilderOverlayService,
        private popupManager: PopupManager,
    ) {
        this.maxPickCount = this.quickBetService.isQuickBetParlayEnabled() ? Infinity : 1;
    }

    addPickHandler$ = createEffect(() => {
        const _this = this;

        const addPickStream$ = this.actions$.pipe(
            ofType(BetslipPickActions.addPick),
            filter((action) => !BetslipGroupPick.isPick(action.pick)),
            concatLatestFrom(() => [this.store.select(betslipPicksListSelector)]),
            map(([action, pickList]) => ({
                pick: action.pick,
                pickCount: _this.getPicksCount(pickList), // the pick we are adding already included in the picklist
            })),
        );

        const requestAddGroupPickStream$ = this.actions$.pipe(
            ofType(BetslipPickActions.requestAddGroupPick),
            concatLatestFrom(() => [this.store.select(betslipPicksListSelector)]),
            map(([, pickList]) => ({
                pick: undefined,
                pickCount: _this.getPicksCount(pickList) + 1, // we need to raise the pick count because the group we are adding is not included in the current picklist.
            })),
        );

        return merge(addPickStream$, requestAddGroupPickStream$).pipe(
            concatLatestFrom(() => [
                this.store.select(quickBetStateSelector),
                this.routerEventsService.currentActivationEnd,
                this.store.select(selectAffiliateOptionRequest),
            ]),
            concatMap(function* ([action, quickBetState, event, affiliateOptionRequest]): Iterable<Action> {
                if (
                    _this.quickBetService.isQuickBetAllowed(event, action.pick) &&
                    (!action.pick || _this.quickBetService.isSupportedPickTypeId(action.pick.id)) &&
                    action.pickCount <= _this.maxPickCount &&
                    !affiliateOptionRequest
                ) {
                    if (quickBetState.uiState === QuickBetUiState.Success) {
                        yield QuickBetActions.closeQuickBet({ cleanErrors: true });
                    }
                    if (
                        quickBetState.uiState === QuickBetUiState.None &&
                        // this is a hack, that is supposed to prevent reopening QB when we already have some picks in the betslip.
                        // It's a hack because maybe in the future we will have a case when we should init the QB after adding 2 picks at once
                        action.pickCount <= 1 &&
                        !(
                            quickBetState.displayHelper.addedToBetslip ||
                            quickBetState.displayHelper.closedByError ||
                            quickBetState.displayHelper.hiddenByError
                        )
                    ) {
                        yield QuickBetActions.init();
                    }
                } else {
                    if (!quickBetState.displayHelper.addedToBetslip) {
                        yield QuickBetActions.closeQuickBet({ cleanErrors: true });
                    }
                }
            }),
        );
    });

    replacePickHandler$ = createEffect(() => {
        const effect = this;

        const replacePickStream$ = this.actions$.pipe(
            ofTypeReplaceExistingPick(),
            filter((action) => !effect.isDifferentPickId(action.pickId.id.toString(), action.newPick.id.toString())),
            map((action) => ({
                pick: action.newPick,
                atBetPlacement: action.atBetPlacement,
            })),
        );
        const addGroupPickStream$ = this.actions$.pipe(
            ofType(BetslipPickActions.addPick),
            filter((action) => BaseBetslipGroupPick.isPick(action.pick)),
            map((action) => ({
                pick: action.pick,
                atBetPlacement: false,
            })),
        );

        return merge(replacePickStream$, addGroupPickStream$).pipe(
            concatLatestFrom(() => this.store.select(betslipSelector)),
            filter(([action, betslipState]) => this.filterReplacePick(action.pick, betslipState)),
            concatMap(function* ([action, betslipState]): Iterable<Action> {
                if (!betslipState.quickBet.pickIds.some((pickId) => pickId.isEqual(action.pick.id))) {
                    yield QuickBetActions.addPick({ pickId: action.pick.id });
                }

                if (!action.atBetPlacement) {
                    const stake = effect.getStake(betslipState, action.pick);

                    yield QuickBetActions.changeStake({ stake, silent: true });
                    if (betslipState.quickBet.uiState === QuickBetUiState.None || betslipState.quickBet.uiState === QuickBetUiState.Loading) {
                        yield QuickBetActions.openQuickBet();
                    }
                }
            }),
        );
    });

    // please don't optimize the replacement of pickIds when they match(eg. when adding a leg to a groups we will eventually trigger a replacePick
    // action that will replace the groupPickId with the new group with same groupPickId). We need this replacement to trigger a combo prevention
    // check for the new pick/group legs
    handleReplacePickIdInQuickBetEffect$ = createEffect(() =>
        this.actions$.pipe(
            ofTypeReplaceExistingPick(),
            map((action) => QuickBetActions.replacePick({ oldPickId: action.pickId, newPickId: action.newPick.id })),
        ),
    );

    handleShowQuickBetEffect$ = createEffect(() =>
        this.actions$.pipe(
            ofType(QuickBetActions.showQuickBet),
            map(() => UiManagerActions.addState({ state: UiStates.QuickBet })),
        ),
    );

    handleRemovePickButtonClickEffect$ = createEffect(() =>
        this.actions$.pipe(
            ofType(QuickBetActions.removePickButtonClick),
            switchMap(function* (_): Iterable<Action> {
                yield QuickBetActions.requestRemoveMultiplePicks({ cleanBetslip: true });
                yield QuickBetActions.closeQuickBet({ cleanErrors: true });
            }),
        ),
    );

    handleClosingQuickBetWhenPickIsRemoved$ = createEffect(() =>
        this.actions$.pipe(
            ofType(BetslipPickActions.removePick, BetslipPickActions.removeMultiplePicks),
            concatLatestFrom(() => [this.store.select(betslipSelector)]),
            filter(([_, betslip]) => betslip.quickBet.uiState !== QuickBetUiState.None && !betslip.picks.pickList.length),
            map((_) => QuickBetActions.closeQuickBet({ cleanErrors: true })),
        ),
    );

    handleRemovePickFromQuickBet$ = createEffect(() =>
        this.actions$.pipe(
            ofType(QuickBetActions.removePick),
            filter((action) => action.cleanBetslip),
            map((action) => BetslipPickActions.requestRemovePick({ pickId: action.pickId })),
        ),
    );

    handleUpdateStakeEffect$ = createEffect(() =>
        this.actions$.pipe(
            ofType(QuickBetActions.removePick),
            concatLatestFrom(() => [this.store.select(selectQuickBetStakeState), this.store.select(selectBetslipFlattenedPicksList)]),
            filter(([action, _, pickList]) => !pickList.some((p) => p.id.isEqual(action.pickId))),
            map(([_, stakeState, _picks]) => this.getStakeAction(stakeState)),
        ),
    );

    handleUpdateStakeLinearBetbuilderRemovalEffect$ = createEffect(() =>
        this.actions$.pipe(
            ofType(BetbuilderActions.removePick),
            concatLatestFrom(() => [
                this.store.select(selectQuickBetStakeState),
                this.store.select(selectBetslipFlattenedPicksList),
                this.store.select(selectIsQuickBetActive),
                this.store.select(selectIsLinearBetslip),
            ]),
            filter(
                ([action, _, pickList, isQuickBetActive, isLinear]) =>
                    isQuickBetActive && isLinear && !pickList.some((p) => p.id.isEqual(action.pickId)),
            ),
            map(([_, stakeState, _pickList, _isQuickBetActive, _isLinear]) => this.getStakeAction(stakeState)),
        ),
    );

    handleRemoveAllPicks$ = createEffect(() =>
        this.actions$.pipe(
            ofType(QuickBetActions.removeMultiplePicks),
            filter((action) => !!action.pickIds.length),
            map(({ pickIds }) => BetslipPickActions.requestRemoveMultiplePicks({ pickIds })),
        ),
    );

    handleRemovePick$ = createEffect(() =>
        this.actions$.pipe(
            ofType(BetslipPickActions.removePick),
            concatLatestFrom(() => this.store.select(quickBetStateSelector)),
            filter(([_, quickBetState]) => quickBetState.uiState !== QuickBetUiState.None),
            map(([action, _]) => QuickBetActions.removePick({ cleanBetslip: false, pickId: action.pickId })),
        ),
    );

    handleRemoveMultiplePicks$ = createEffect(() =>
        this.actions$.pipe(
            ofType(BetslipPickActions.removeMultiplePicks),
            concatLatestFrom(() => this.store.select(quickBetStateSelector)),
            filter(([_, quickBetState]) => quickBetState.uiState !== QuickBetUiState.None),
            concatMap(([action, _]) => action.pickIds.map((pickId) => QuickBetActions.removePick({ cleanBetslip: false, pickId }))),
        ),
    );

    handleResetQuickBetWhenAllPickRemoved$ = createEffect(() =>
        this.actions$.pipe(
            ofType(BetslipPickActions.removePick, BetslipPickActions.removeMultiplePicks),
            concatLatestFrom(() => this.store.select(betslipSelector)),
            filter(([_, betslip]) => !betslip.picks.pickList.length),
            map((_) => QuickBetActions.setDisplayHelperToDefault()),
        ),
    );

    handleGroupPickRemovalOverlay$ = createEffect(() =>
        this.actions$.pipe(
            ofType(removeAndReplaceGroup),
            concatLatestFrom(() => this.store.select(quickBetStateSelector)),
            // in the overlay we want to keep the groupId
            filter(([_, quickBetState]) => quickBetState.uiState !== QuickBetUiState.None),
            map(([action, _]) => QuickBetActions.replacePick({ newPickId: action.newPick.id, oldPickId: action.pickId })),
        ),
    );

    handleOpenQuickBetEffect$ = createEffect(() => {
        const _this = this;

        return this.actions$.pipe(
            ofType(QuickBetActions.openQuickBet),
            concatLatestFrom(() => this.store.select(quickBetStateSelector)),

            switchMap(function* ([action, quickBetState]): Iterable<Action> {
                // Notify wrapper
                _this.quickBetService.dispatchSensitivePage('open');

                if (quickBetState.keepOpen) {
                    yield QuickBetActions.keepOpen({ open: false });
                }

                const quickBetStore = _this.localStore.get<QuickBetStore>('quickBet') || { helpShown: false };

                if (!quickBetStore.helpShown) {
                    quickBetStore.helpShown = true;
                    _this.localStore.set<QuickBetStore>('quickBet', quickBetStore);
                    yield QuickBetActions.showHelp();
                }

                yield QuickBetActions.showWalletSelector();
            }),
        );
    });

    handleShowWalletSelectorEffect$ = createEffect(
        () => {
            const settings: DialogSettings = { cssClass: 'wallet-selector-mobile-betslip' };

            return this.actions$.pipe(
                ofType(QuickBetActions.showWalletSelector),
                tap(() => {
                    if (this.walletSelectorDialogService.showWalletSelector) {
                        this.walletSelectorDialogService.open(settings);
                    }
                }),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    handleHideQuickBetEffect$ = createEffect(() =>
        this.actions$.pipe(
            ofType(QuickBetActions.hideQuickBet),
            map(() => UiManagerActions.removeState({ state: UiStates.QuickBet })),
        ),
    );

    handleAddToBetslipQuickBetEffect$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(QuickBetActions.closeQuickbetUi, QuickBetActions.closeQuickBetUiOverlay),
                tap(() => {
                    // Notify wrapper
                    this.quickBetUtils.cleanBetslipErrors();
                    this.quickBetService.dispatchSensitivePage('close');
                    this.quickBetUtils.tryReleaseScroll();
                }),
                ignoreElementsTyped(),
            ),
        { dispatch: false },
    );

    handleCloseQuickbetUI$ = createEffect(() =>
        this.actions$.pipe(
            ofType(QuickBetActions.closeQuickbetUi),
            map(() => QuickBetActions.cleanQuickBetAfterCloseUI()),
        ),
    );

    handleCloseQuickBetEffect$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(QuickBetActions.closeQuickBet),
                tap((action) => {
                    if (action.cleanErrors) {
                        this.quickBetUtils.cleanBetslipErrors();
                    }
                    // Notify wrapper
                    this.quickBetService.dispatchSensitivePage('close');
                    this.quickBetUtils.tryReleaseScroll();
                }),
            ),
        { dispatch: false },
    );

    handleCloseQuickBetOnLobbyEffect$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UiManagerActions.removeState),
            concatLatestFrom(() => this.routerEventsService.currentActivationEnd),
            filter(
                ([action, event]) =>
                    action.state === UiStates.SheetViewOpened && !this.quickBetService.validateQuickBetAvailability(event?.snapshot?.data['tag']),
            ),
            map((_) => QuickBetActions.closeQuickBet({ cleanErrors: true })),
        ),
    );

    handleCloseConfirmationEffect$ = createEffect(() =>
        this.actions$.pipe(
            ofType(QuickBetActions.closeConfirmation),
            map(() => {
                // Notify wrapper
                this.quickBetService.dispatchSensitivePage('close');
                this.quickBetUtils.tryReleaseScroll();

                return QuickBetActions.requestRemoveMultiplePicks({ cleanBetslip: true });
            }),
        ),
    );

    handleHelpCloseClickEffect$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                QuickBetActions.helpCloseClick,
                RewardTokensActions.openDialog,
                RewardTokensActions.showOverlayInfo,
                QuickBetActions.hideQuickBet,
                BetslipPickActions.expandBetBuilderLegs,
            ),
            concatLatestFrom(() => this.store.select(showQuickBetHelpSelector)),
            filter(([_, helpState]) => helpState.isVisible),
            map(() => QuickBetActions.hideHelp()),
        ),
    );

    handleViewBonusEffect$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(QuickBetActions.viewBonus),
                tap(() => this.redirectHelperService.goToPage(this.productHomepagesConfig.portal + '/mobileportal/mybonuses')),
                ignoreElementsTyped(),
            ),
        { dispatch: false },
    );

    handleUserLoggedInEffect$ = createEffect(() => {
        const epic = this;

        return this.actions$.pipe(
            ofType(UserActions.userLoggedIn),
            concatLatestFrom(() => this.store.select(quickBetStateSelector)),

            concatMap(function* ([, quickBetState]): Iterable<Action> {
                const currentStake = quickBetState.stakeState.stake;
                const balance = epic.accountBalanceService.currentBalanceAmount;
                if (Number(currentStake) > balance) {
                    yield QuickBetActions.changeStake({ stake: currentStake! });
                }
            }),
        );
    });

    handleUserLoggedOutEffect$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserActions.userLoggedOut),
            concatLatestFrom(() => this.store.select(quickBetStateSelector)),
            concatMap(([_, quickBetState]) => {
                if (quickBetState.uiState !== QuickBetUiState.None) {
                    return [
                        QuickBetActions.setActionButton({ payload: QuickBetActionButtonState.Login }),
                        QuickBetActions.resetUiState(),
                        QuickBetActions.resetResultState(),
                        RewardTokensActions.deselectAllToken(),
                    ];
                }

                return [];
            }),
        ),
    );

    handleBetslipErrorsCheckFinishedEffect$ = createEffect(() =>
        this.actions$.pipe(
            ofType(BetslipErrorsActions.betslipErrorsCheckFinished),
            concatLatestFrom(() => [this.store.select(selectQuickBetActionButtonProcessing), this.store.select(selectQuickBetLockedOrClosedState)]),
            concatMap(([_, actionButtonProcessing, hasClosedOrLockedPick]) => {
                if (actionButtonProcessing) {
                    return [];
                }

                return [
                    QuickBetActions.setHasClosedPick({ payload: { hasClosedPick: hasClosedOrLockedPick.isClosed } }),
                    QuickBetActions.setHasLockedPick({ payload: { hasLockedPick: hasClosedOrLockedPick.isLocked } }),
                ];
            }),
        ),
    );

    handleAcceptMarketClosedChanges$ = createEffect(() =>
        this.actions$.pipe(
            ofType(QuickBetActions.acceptMarketClosedChanges),
            concatLatestFrom(() => this.store.select(selectQuickBetPicksWithErrors)),
            concatMap(([_, picksWithErrors]) => {
                const actions: Action[] = [];
                for (const pickWithErrors of picksWithErrors) {
                    const groupPickOfferChange = pickWithErrors.errors.filter(isGroupPickOfferChange);

                    if (groupPickOfferChange.length) {
                        actions.push(
                            removePicksFromGroup({
                                groupInfo: pickWithErrors.pick.groupInfo!,
                                pickIds: groupPickOfferChange.flatMap((error) => error.legIds) as GroupEnabledPickIds[],
                            }),
                        );
                    } else if (pickWithErrors.errors.some(isPickStatusLocked)) {
                        actions.push(QuickBetActions.removePick({ cleanBetslip: true, pickId: pickWithErrors.pick.id }));
                    }
                }

                return actions;
            }),
        ),
    );

    handleGroupPickLegChanges$ = createEffect(() =>
        this.actions$.pipe(
            ofType(addGroup),
            concatMap((action) => action.newPick.getLegs().map((pick) => QuickBetActions.removePick({ cleanBetslip: false, pickId: pick.id }))),
        ),
    );

    handleGoToMyBets$ = createEffect(() =>
        this.actions$.pipe(
            ofType(QuickBetActions.goToMyBets),
            concatLatestFrom(() => this.store.select(selectKeepsAnyBetsInBetslip)),
            tap(([action]) => {
                if (action.inPopup) {
                    this.popupManager.open(ModelPopupTypes.MyBets);
                } else {
                    this.redirectHelperService.goToMyBets(MyBetsUrlParam.Open, undefined, false);
                }
            }),
            concatMap(([, keepInBetslip]) => {
                const actions: Action[] = [QuickBetActions.closeQuickbetUi()];
                if (!keepInBetslip) {
                    actions.push(QuickBetActions.requestRemoveMultiplePicks({ cleanBetslip: true }));
                }

                return actions;
            }),
        ),
    );

    handleCloseResultScreen$ = createEffect(() =>
        this.actions$.pipe(
            ofType(QuickBetActions.closeResultScreen),
            concatLatestFrom(() => [this.store.select(selectKeepsAnyBetsInBetslip)]),
            concatMap(([, keepInBetslip]) => {
                const actions: Action[] = [];

                if (!keepInBetslip) {
                    actions.push(QuickBetActions.closeQuickbetUi());
                    actions.push(QuickBetActions.requestRemoveMultiplePicks({ cleanBetslip: true }));
                } else {
                    actions.push(QuickBetActions.resetToPlaceState());
                    actions.push(BetslipErrorsActions.validateBetslip());
                }

                return actions;
            }),
        ),
    );

    handleRequestRemoveMultiplePicks$ = createEffect(() =>
        this.actions$.pipe(
            ofType(QuickBetActions.requestRemoveMultiplePicks),
            concatLatestFrom(() => this.store.select(selectQuickBetPickIds)),
            map(([action, pickIds]) => QuickBetActions.removeMultiplePicks({ pickIds: action.cleanBetslip ? pickIds : [] })),
        ),
    );

    handleAddToBetslipOverlayEffect$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(QuickBetActions.closeQuickBetUiOverlay),
                tap((_) => {
                    if (this.popupManager.isPopupInStack('betslip')) {
                        this.betBuilderOverlayService.close();
                    } else {
                        this.betBuilderOverlayService.dismissConfirmationScreen();
                        this.popupManager.open('betslip');
                    }

                    this.quickBetService.toggleQuickBetVisibility(false);
                }),
                ignoreElementsTyped(),
            ),
        { dispatch: false },
    );

    handleOpenBetslipEffect$ = createEffect(() =>
        this.actions$.pipe(
            ofType(QuickBetActions.openBetslip),
            tap(async (_) => {
                const isOverlayOpen = this.popupManager.isPopupOpen('bet-builder-overlay');
                const isBetslipInStack = this.popupManager.isPopupInStack('betslip');
                // if we're opening the betslip from Overlay, and we already have the betslip popup open, then we just close the overlay, we expect to land on the betslip
                if (isOverlayOpen && isBetslipInStack) {
                    await this.popupManager.closeByName(PopupAction.SilentClose, 'betslip');
                }
                this.popupManager.open('betslip', undefined, { settings: { closePrevious: false } });
            }),
            map(() => QuickBetActions.hideQuickBet()),
        ),
    );

    handleQuickBetReopenOnUndo$ = createEffect(() =>
        this.actions$.pipe(
            ofType(BetslipResultActions.close, DigitalActions.closeBetslipTriggered),
            concatLatestFrom(() => [this.store.select(selectQuickBetDisplayHelper), this.store.select(betslipSelector)]),
            filter(([, displayHelper, betslipState]) => displayHelper.addedToBetslip && !!betslipState.picks.pickList.length),
            tap(() => {
                this.quickBetService.toggleQuickBetVisibility(true);
            }),
            concatMap(([a, b, betslipState]) => {
                const actions: Action[] = [];
                const firstPick = betslipState.picks.pickList[0]!;
                const betslipType =
                    betslipState.picks.pickList.length === 1 &&
                    !isBaseBetslipGroupPick(firstPick) &&
                    !(betslipState.base.isSportcastAsComboEnabled && isBetslipBetBuilderPick(firstPick))
                        ? BetslipType.Single
                        : BetslipType.Combo;

                if (betslipState.base.displayMode === BetslipDisplayMode.Tabbed && betslipState.types.base.currentSelectedType !== betslipType) {
                    actions.push(BetslipTypesActions.setSelectedType({ betslipType, selectedByUser: false, systemInfo: null }));
                }
                if (Object.values(betslipState.types.comboBet.picks).some((value) => !value.isSelected)) {
                    actions.push(BetslipPickActions.toggleAllPicks({ select: true }));
                }
                actions.push(QuickBetActions.resetAddedToBetslip());

                return actions;
            }),
        ),
    );

    private filterReplacePick(newPick: BetslipPick, betslipState: IBetslipState) {
        return (
            betslipState.quickBet.uiState !== QuickBetUiState.Success &&
            betslipState.quickBet.uiState !== QuickBetUiState.None &&
            (this.getPicksCount(betslipState.picks.pickList) <= this.maxPickCount || BetslipBetBuilderPick.isEntainUiBetBuilderPick(newPick)) &&
            !(
                betslipState.quickBet.displayHelper.addedToBetslip ||
                betslipState.quickBet.displayHelper.closedByError ||
                betslipState.quickBet.displayHelper.hiddenByError
            )
        );
    }

    private getStake(betslipState: IBetslipState, newPick: BetslipPick): string {
        if (betslipState.quickBet.stakeState.stake) {
            return betslipState.quickBet.stakeState.stake;
        } else if (this.getPicksCount(betslipState.picks.pickList) > 1 && BetslipBetBuilderPick.isEntainUiBetBuilderPick(newPick)) {
            return this.userSettings.defaultStake?.toString() || '';
        }

        return '';
    }

    private getPicksCount(picks: BetslipPick[]): number {
        return picks.length;
    }

    private isDifferentPickId(oldPickId: string, newPickId: string): boolean {
        return oldPickId.toString() !== newPickId.toString();
    }

    private getStakeAction(stakeState: IQuickBetStakeState) {
        const stake = stakeState.stake || this.userSettings.defaultStake?.toString() || '';

        return QuickBetActions.changeStake({ stake, silent: true });
    }
}
